import { useMemo } from 'react'
import { useQuery, skipToken } from '@apollo/client/react'
import { HasPermissionsHookDocument } from './useHasPermission.graphql'
import {
  type BankAccountsPermissionsEnum,
  type BillingPlanPermissionsEnum,
  type BusinessDetailsPermissionsEnum,
  type BusinessFeaturesPermissionsEnum,
  type RulesPermissionsEnum,
  type TeamPermissionsEnum,
  type PagePermissionsEnum,
  type DashboardPermissionsEnum,
  type InvoicesPermissionsEnum,
  type PaymentsPermissionsEnum,
  type LocalBusinessesPermissionsEnum,
  type SettleInboxPermissionsEnum,
  type SettleInboxConfigPermissionsEnum,
  type VendorCreditsPermissionsEnum,
  type PurchaseOrderPermissionsEnum,
  type ReceiptPermissionsEnum,
  type AccountingPermissionsEnum,
  type ClientManagerPermissionsEnum,
  type InventoryPermissionsEnum,
  type DiscrepancyRulePermissionsEnum,
  type InventoryTransferPermissionsEnum,
  type WorkOrderPermissionsEnum,
  type SalesChannelConfigurationPermissionsEnum,
  type OutboundOrderPermissionsEnum,
  type ReconciliationItemsPermissionsEnum,
  type AttachmentDocumentTemplatePermissionsEnum,
  type ReceivablesPermissionsEnum,
} from 'gql/types'

export type Permission =
  | BankAccountsPermissionsEnum
  | BillingPlanPermissionsEnum
  | BusinessDetailsPermissionsEnum
  | BusinessFeaturesPermissionsEnum
  | RulesPermissionsEnum
  | TeamPermissionsEnum
  | PagePermissionsEnum
  | DashboardPermissionsEnum
  | InvoicesPermissionsEnum
  | PaymentsPermissionsEnum
  | LocalBusinessesPermissionsEnum
  | SettleInboxPermissionsEnum
  | SettleInboxConfigPermissionsEnum
  | VendorCreditsPermissionsEnum
  | PurchaseOrderPermissionsEnum
  | ReceiptPermissionsEnum
  | AccountingPermissionsEnum
  | ClientManagerPermissionsEnum
  | InventoryPermissionsEnum
  | DiscrepancyRulePermissionsEnum
  | InventoryTransferPermissionsEnum
  | WorkOrderPermissionsEnum
  | SalesChannelConfigurationPermissionsEnum
  | OutboundOrderPermissionsEnum
  | ReconciliationItemsPermissionsEnum
  | AttachmentDocumentTemplatePermissionsEnum
  | ReceivablesPermissionsEnum

export interface PermissionsHookOptions {
  check?: 'some' | 'every'
  skip?: boolean
}

export const useHasPermission = (
  permission: Permission | Permission[],
  { check = 'every', skip = false }: PermissionsHookOptions = {},
): boolean => {
  const hasNothingToCheck = Array.isArray(permission) && permission.length === 0
  const shouldSkip = skip || hasNothingToCheck
  const { data } = useQuery(HasPermissionsHookDocument, shouldSkip ? skipToken : {})

  const permissions: Permission[] = useMemo(() => {
    const permissions: Permission[] = []
    const userPermissions = data?.permissions || {}

    Object.values(userPermissions).forEach((perms) => {
      if (perms instanceof Array) {
        permissions.push(...perms)
      }
    })

    return permissions
  }, [data?.permissions])

  if (permission instanceof Array) {
    return check === 'every'
      ? permission.every((perm) => permissions.includes(perm))
      : permission.some((perm) => permissions.includes(perm))
  }

  return permissions.includes(permission)
}
